/**
 * <expiring-coupon-scratch>
 *
 * Gamifies the "Expiring Coupon" product block: the discount code is
 * covered by a canvas "scratch card". Customers reveal it by:
 *  - moving the mouse across it (desktop, no click needed)
 *  - swiping a finger across it (mobile, native touch drag)
 *  - clicking / tapping it (instant full reveal fallback)
 *  - focusing it and pressing Enter or Space (keyboard fallback)
 *
 * Resets on every page load — nothing is persisted between visits.
 */
class ExpiringCouponScratch extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('.expiring-coupon__trigger');
    this.canvas = this.querySelector('.expiring-coupon__canvas');
    this.container = this.closest('.expiring-coupon');

    if (!this.button || !this.canvas || !this.canvas.getContext) return;

    this.ctx = this.canvas.getContext('2d');
    this.revealed = false;
    this.scratchedCells = new Set();
    this.gridCols = 12;
    this.gridRows = 4;
    this.revealThreshold = 0.5;

    this.handleResize = this.sizeCanvas.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);

    // Size after layout/fonts settle so the canvas matches the button exactly.
    requestAnimationFrame(() => this.sizeCanvas());
    window.addEventListener('resize', this.handleResize);

    this.canvas.addEventListener('pointermove', this.handlePointerMove);
    this.button.addEventListener('click', this.handleClick);
    this.button.addEventListener('keydown', this.handleKeydown);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
  }

  sizeCanvas() {
    if (this.revealed) return;
    const rect = this.button.getBoundingClientRect();
    const width = Math.max(Math.round(rect.width), 1);
    const height = Math.max(Math.round(rect.height), 1);
    const dpr = window.devicePixelRatio || 1;

    this.canvas.width = width * dpr;
    this.canvas.height = height * dpr;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.drawScratchSurface(width, height);
  }

  drawScratchSurface(width, height) {
    const ctx = this.ctx;
    const styles = getComputedStyle(this.container || this);
    const accent = styles.getPropertyValue('--expiring-coupon-accent').trim() || '#D1BCB5';
    const accent2 = styles.getPropertyValue('--expiring-coupon-accent-2').trim() || '#BA745F';

    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, width, height);

    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, accent);
    gradient.addColorStop(1, accent2);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // Subtle diagonal texture so the cover reads as "scratchable".
    ctx.strokeStyle = 'rgba(255,255,255,0.28)';
    ctx.lineWidth = 1;
    for (let x = -height; x < width; x += 6) {
      ctx.beginPath();
      ctx.moveTo(x, height);
      ctx.lineTo(x + height, 0);
      ctx.stroke();
    }
  }

  handlePointerMove(event) {
    if (this.revealed) return;
    const rect = this.canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    if (x < 0 || y < 0 || x > rect.width || y > rect.height || !rect.width || !rect.height) return;

    const ctx = this.ctx;
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, 16, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    const col = Math.min(this.gridCols - 1, Math.max(0, Math.floor((x / rect.width) * this.gridCols)));
    const row = Math.min(this.gridRows - 1, Math.max(0, Math.floor((y / rect.height) * this.gridRows)));
    this.scratchedCells.add(row + '-' + col);

    const scratchedRatio = this.scratchedCells.size / (this.gridCols * this.gridRows);
    if (scratchedRatio >= this.revealThreshold) {
      this.reveal();
    }
  }

  handleClick() {
    this.reveal();
  }

  handleKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      this.reveal();
    }
  }

  reveal() {
    if (this.revealed) return;
    this.revealed = true;

    this.canvas.classList.add('is-hidden');
    this.canvas.removeEventListener('pointermove', this.handlePointerMove);
    window.removeEventListener('resize', this.handleResize);
    this.button.setAttribute('aria-label', this.button.dataset.revealedLabel || 'Discount code revealed');

    const codeEl = this.querySelector('.expiring-coupon__code');
    const code = codeEl ? codeEl.textContent.trim() : '';

    if (this.container) {
      this.container.classList.add('is-revealed');

      const bannerIcon = this.container.querySelector('.expiring-coupon__banner-icon');
      const bannerText = this.container.querySelector('.expiring-coupon__banner-text');
      let revealedMessage = '';
      if (bannerText) {
        revealedMessage = bannerText.dataset.textRevealed || bannerText.textContent;
        bannerText.textContent = revealedMessage;
      }
      if (bannerIcon) {
        bannerIcon.textContent = bannerIcon.dataset.iconRevealed || bannerIcon.textContent;
      }

      const live = this.container.querySelector('.expiring-coupon__live');
      if (live) {
        live.textContent = code
          ? 'Code revealed: ' + code + '. ' + revealedMessage
          : revealedMessage;
      }
    }
  }
}

if (!customElements.get('expiring-coupon-scratch')) {
  customElements.define('expiring-coupon-scratch', ExpiringCouponScratch);
}
